/**
 * Svea Payments method codes (GetPaymentMethods) shown instead of the generic
 * online-payment label (#3961).
 *
 * A recorded payment keeps `payment_method = online` and stores the code in
 * the note as `svea_method:<code>`. The payment-request row stores the same
 * code in `sveaPaymentMethod`.
 */

export type SveaPaymentMethodKind =
  | 'svea_invoice'
  | 'svea_part_payment'
  | 'svea_b2b_invoice'
  | 'card'
  | 'mobile'
  | 'bank'
  | 'paypal';

const SVEA_METHOD_NOTE_PREFIX = 'svea_method:';
const AN4 = /^[A-Z0-9]{4}$/;

/** Codes that are not the FI01–FI49 online-bank range. */
const KIND_BY_CODE: Record<string, SveaPaymentMethodKind> = {
  FI70: 'svea_invoice',
  FI71: 'svea_part_payment',
  FI72: 'svea_b2b_invoice',
  FI50: 'card',
  FI51: 'card',
  FI52: 'card',
  FI53: 'card',
  FI54: 'mobile',
  PIVO: 'mobile',
  SIIR: 'mobile',
  USPP: 'paypal',
};

const LABEL_KEY: Record<SveaPaymentMethodKind, string> = {
  svea_invoice: 'sveaPaymentMethod.svea_invoice',
  svea_part_payment: 'sveaPaymentMethod.svea_part_payment',
  svea_b2b_invoice: 'sveaPaymentMethod.svea_b2b_invoice',
  card: 'sveaPaymentMethod.card',
  mobile: 'sveaPaymentMethod.mobile',
  bank: 'sveaPaymentMethod.bank',
  paypal: 'sveaPaymentMethod.paypal',
};

function normalizeCode(code: string): string | null {
  const normalized = code.trim().toUpperCase();
  return AN4.test(normalized) ? normalized : null;
}

/** `null` for an empty, malformed or unknown code. */
export function sveaPaymentMethodKind(code: string | null | undefined): SveaPaymentMethodKind | null {
  if (typeof code !== 'string') return null;
  const normalized = normalizeCode(code);
  if (!normalized) return null;

  const mapped = KIND_BY_CODE[normalized];
  if (mapped) return mapped;

  const bank = /^FI(\d{2})$/.exec(normalized);
  if (!bank) return null;
  const number = Number(bank[1]);
  return number >= 1 && number <= 49 ? 'bank' : null;
}

/**
 * The AN4 code from a payment note, or `null` when the note is not exactly
 * `svea_method:<code>` (optional surrounding whitespace).
 */
export function sveaMethodCodeFromNote(note: string | null | undefined): string | null {
  if (typeof note !== 'string') return null;
  const trimmed = note.trim();
  if (!trimmed.startsWith(SVEA_METHOD_NOTE_PREFIX)) return null;
  return normalizeCode(trimmed.slice(SVEA_METHOD_NOTE_PREFIX.length));
}

/** `common.json` key under `sveaPaymentMethod`. */
export function sveaPaymentMethodLabelKey(kind: SveaPaymentMethodKind): string {
  return LABEL_KEY[kind];
}
